import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import {
  KanbanSquare, LayoutGrid, Table2, Building2, Users, Plus, Trash2,
  TrendingUp, AlertTriangle,
} from "lucide-react";

const STAGE_META = {
  nouveau: { label: "Nouveau", dot: "bg-neutral-400" },
  en_cours: { label: "En cours", dot: "bg-sky-500" },
  inscrit: { label: "Inscrit", dot: "bg-emerald-500" },
  refuse: { label: "Refusé", dot: "bg-red-400" },
  injoignable: { label: "Injoignable", dot: "bg-neutral-500" },
};

const RELATION_META = {
  actif: { label: "Actif", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  a_relancer: { label: "À relancer", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  inactif: { label: "Inactif", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
};

const SOLVENCY_META = {
  ok: { label: "À jour", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  en_attente: { label: "En attente", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  impaye: { label: "Impayé", cls: "bg-red-50 text-red-700 border-red-200" },
};

const SOURCE_STYLE = {
  reservation: "bg-blue-100 text-blue-700",
  waitlist: "bg-emerald-100 text-emerald-700",
  contact: "bg-amber-100 text-amber-700",
};

function SourceBadge({ c }) {
  if (!c.source_label) return null;
  return (
    <span data-testid={`crm-source-${c.id}`}
      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${SOURCE_STYLE[c.source_type] || "bg-neutral-100 text-neutral-600"}`}>
      {c.source_label}
    </span>
  );
}

function StageSelect({ c, stages, onPatch, testId }) {
  return (
    <select
      data-testid={testId}
      value={c.stage}
      onChange={(e) => onPatch(c.id, { crm_status: e.target.value }, `Déplacé vers « ${(STAGE_META[e.target.value] || {}).label || e.target.value} »`)}
      className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs outline-none focus:border-brand"
    >
      {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
    </select>
  );
}

const eur = (n) => `${(Number(n) || 0).toLocaleString("fr-FR")} €`;

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-4">
      <div className="flex items-center gap-2 text-neutral-400">
        <Icon className="h-4 w-4" />
        <span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <p className={`mt-1 text-2xl font-extrabold ${accent || "text-neutral-900"}`}>{value}</p>
    </div>
  );
}

function ProspectCard({ c, employees, stages, onPatch, onDelete }) {
  return (
    <div data-testid={`crm-card-${c.id}`} className="rounded-lg border border-neutral-200 bg-white p-3 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <Link to={`/admin/leads/${c.id}`} className="font-semibold text-neutral-900 hover:text-brand">
          {c.first_name} {c.last_name}
        </Link>
        {c.urgent && <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-bold uppercase text-orange-700">Urgent</span>}
      </div>
      <div className="mt-1"><SourceBadge c={c} /></div>
      <p className="mt-0.5 text-xs text-neutral-500">{c.city || "—"} · {c.phone || c.email}</p>
      {c.assigned_school_name && <p className="mt-1 text-xs text-neutral-600">→ {c.assigned_school_name}</p>}
      <div className="mt-2 flex items-center gap-2">
        <select
          data-testid={`crm-owner-${c.id}`}
          value={c.owner_email || ""}
          onChange={(e) => onPatch(c.id, { owner_email: e.target.value }, "Responsable mis à jour")}
          className="min-w-0 flex-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs outline-none focus:border-brand"
        >
          <option value="">{"— Responsable —"}</option>
          {employees.map((e) => <option key={e.id} value={e.email}>{e.name || e.email}</option>)}
        </select>
        {c.stage === "nouveau" && (
          <button onClick={() => onPatch(c.id, { mark_contacted: true }, "Marqué comme contacté")}
            data-testid={`crm-contact-${c.id}`}
            className="shrink-0 rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-700 hover:bg-sky-100">
            Contacté
          </button>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[11px] text-neutral-500">Étape</span>
        <StageSelect c={c} stages={stages} onPatch={onPatch} testId={`crm-stage-${c.id}`} />
        <button onClick={() => onDelete(c)} data-testid={`crm-delete-${c.id}`} title="Supprimer la fiche"
          className="ml-auto rounded-md border border-neutral-200 p-1 text-neutral-400 hover:border-red-400 hover:text-red-600">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {c.next_action && <p className="mt-1.5 text-[11px] text-neutral-500">⏱ {c.next_action}{c.next_action_at ? ` · ${c.next_action_at}` : ""}</p>}
    </div>
  );
}

function StageBadge({ stage }) {
  const m = STAGE_META[stage] || STAGE_META.nouveau;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700">
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} /> {m.label}
    </span>
  );
}

/* ------------------------------- Prospects ------------------------------- */
function ProspectsTab() {
  const [stages, setStages] = useState([]);
  const [columns, setColumns] = useState({});
  const [employees, setEmployees] = useState([]);
  const [view, setView] = useState("kanban");
  const [activeStage, setActiveStage] = useState("nouveau");
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      const [{ data }, emp] = await Promise.all([
        api.get("/admin/crm/board"),
        api.get("/admin/qualifiers"),
      ]);
      setStages(data.stages);
      setColumns(data.columns);
      setEmployees(emp.data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const patch = async (id, body, msg) => {
    try {
      await api.post(`/admin/leads/${id}/crm`, body);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la fiche prospect ${c.first_name} ${c.last_name} ? (la demande d'origine est conservée)`)) return;
    try {
      await api.delete(`/admin/leads/${c.id}`);
      toast.success("Fiche supprimée");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const count = (k) => (columns[k] || []).length;
  const tableRows = view === "table" ? (columns[activeStage] || []) : [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" data-testid="crm-stage-chips">
          {stages.map((s) => (
            <button key={s.key} onClick={() => setActiveStage(s.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                view === "table" && activeStage === s.key ? "border-brand bg-brand text-white" : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400"
              }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${(STAGE_META[s.key] || {}).dot}`} />
              {s.label} <span className="opacity-60">{count(s.key)}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-1 rounded-md border border-neutral-200 bg-white p-0.5">
          <button onClick={() => setView("kanban")} data-testid="crm-view-kanban"
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold ${view === "kanban" ? "bg-brand text-white" : "text-neutral-600"}`}>
            <LayoutGrid className="h-3.5 w-3.5" /> Kanban
          </button>
          <button onClick={() => setView("table")} data-testid="crm-view-table"
            className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold ${view === "table" ? "bg-brand text-white" : "text-neutral-600"}`}>
            <Table2 className="h-3.5 w-3.5" /> Tableau
          </button>
        </div>
      </div>

      {view === "kanban" ? (
        <div className="mt-5 flex gap-4 overflow-x-auto pb-3" data-testid="crm-kanban">
          {stages.map((s) => (
            <div key={s.key} className="w-72 shrink-0">
              <div className="mb-2 flex items-center gap-2 px-1">
                <span className={`h-2 w-2 rounded-full ${(STAGE_META[s.key] || {}).dot}`} />
                <span className="text-sm font-bold text-neutral-800">{s.label}</span>
                <span className="text-xs text-neutral-400">{count(s.key)}</span>
              </div>
              <div className="space-y-2 rounded-xl bg-neutral-100/60 p-2 min-h-[80px]">
                {(columns[s.key] || []).map((c) => <ProspectCard key={c.id} c={c} employees={employees} stages={stages} onPatch={patch} onDelete={remove} />)}
                {count(s.key) === 0 && <p className="px-2 py-3 text-center text-xs text-neutral-400">Vide</p>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-4 py-3">Prospect</th>
                <th className="px-4 py-3">Étape</th>
                <th className="px-4 py-3">Responsable</th>
                <th className="px-4 py-3">Prochaine action</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {tableRows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Aucun prospect à cette étape.</td></tr>
              )}
              {tableRows.map((c) => {
                const d = drafts[c.id] || { next_action: c.next_action || "", next_action_at: c.next_action_at || "" };
                return (
                  <tr key={c.id} data-testid={`crm-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/leads/${c.id}`} className="font-semibold text-neutral-900 hover:text-brand">{c.first_name} {c.last_name}</Link>
                      <div className="text-xs text-neutral-500">{c.city || "—"} · {c.phone || c.email}</div>
                      <div className="mt-1"><SourceBadge c={c} /></div>
                    </td>
                    <td className="px-4 py-3">
                      <StageSelect c={c} stages={stages} onPatch={patch} testId={`crm-row-stage-${c.id}`} />
                    </td>
                    <td className="px-4 py-3">
                      <select value={c.owner_email || ""} onChange={(e) => patch(c.id, { owner_email: e.target.value }, "Responsable mis à jour")}
                        className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs outline-none focus:border-brand">
                        <option value="">{"—"}</option>
                        {employees.map((e) => <option key={e.id} value={e.email}>{e.name || e.email}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <input value={d.next_action} placeholder="Action…"
                          onChange={(e) => setDrafts((p) => ({ ...p, [c.id]: { ...d, next_action: e.target.value } }))}
                          className="w-32 rounded-md border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-brand" />
                        <input type="date" value={d.next_action_at ? d.next_action_at.slice(0, 10) : ""}
                          onChange={(e) => setDrafts((p) => ({ ...p, [c.id]: { ...d, next_action_at: e.target.value } }))}
                          className="rounded-md border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-brand" />
                        <button onClick={() => patch(c.id, { next_action: d.next_action, next_action_at: d.next_action_at }, "Action enregistrée")}
                          className="rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-neutral-700">OK</button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.stage === "nouveau" && (
                        <button onClick={() => patch(c.id, { mark_contacted: true }, "Marqué comme contacté")}
                          className="rounded-md border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-700 hover:bg-sky-100">Contacté</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ Auto-écoles ------------------------------ */
function SchoolsTab() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/crm/schools");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (id, body, msg) => {
    try {
      await api.post(`/admin/schools/${id}/crm`, body);
      if (msg) toast.success(msg);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
          <tr>
            <th className="px-4 py-3">Auto-école</th>
            <th className="px-4 py-3 text-center">Reçus</th>
            <th className="px-4 py-3 text-center">Signés</th>
            <th className="px-4 py-3 text-center">Conversion</th>
            <th className="px-4 py-3 text-center">Solvabilité</th>
            <th className="px-4 py-3">Relation</th>
            <th className="px-4 py-3">Note interne</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100">
          {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">Aucune auto-école.</td></tr>}
          {rows.map((s) => {
            const sol = SOLVENCY_META[s.solvency] || SOLVENCY_META.ok;
            const note = drafts[s.id] !== undefined ? drafts[s.id] : (s.crm_note || "");
            return (
              <tr key={s.id} data-testid={`crm-school-${s.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-neutral-900">{s.name}</div>
                  <div className="text-xs text-neutral-500">{s.city}{s.phone ? ` · ${s.phone}` : ""}</div>
                  <div className="mt-0.5 text-[11px] text-neutral-400">Généré {eur(s.commissions_generated)} · Payé {eur(s.commissions_paid)}{s.unpaid_invoices ? ` · Dû ${eur(s.unpaid_invoices)}` : ""}</div>
                </td>
                <td className="px-4 py-3 text-center font-semibold">{s.received}</td>
                <td className="px-4 py-3 text-center font-semibold text-emerald-700">{s.signed}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`font-bold ${s.conversion >= 30 ? "text-emerald-700" : s.conversion > 0 ? "text-amber-700" : "text-neutral-400"}`}>{s.conversion}%</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${sol.cls}`}>{sol.label}</span>
                </td>
                <td className="px-4 py-3">
                  <select value={s.relation || ""} onChange={(e) => save(s.id, { crm_relation: e.target.value }, "Relation mise à jour")}
                    className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs outline-none focus:border-brand">
                    {Object.entries(RELATION_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <input value={note} placeholder="Note…" onChange={(e) => setDrafts((p) => ({ ...p, [s.id]: e.target.value }))}
                      className="w-40 rounded-md border border-neutral-300 px-2 py-1 text-xs outline-none focus:border-brand" />
                    <button onClick={() => save(s.id, { crm_note: note }, "Note enregistrée")}
                      className="rounded-md bg-neutral-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-neutral-700">OK</button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------- Employés -------------------------------- */
function EmployeesTab() {
  const [rows, setRows] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/qualifiers");
      setRows(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Nom, email et mot de passe (6 caractères min) requis.");
      return;
    }
    try {
      await api.post("/admin/qualifiers", form);
      toast.success("Employé créé.");
      setForm({ name: "", email: "", password: "" });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggle = async (r) => {
    try {
      await api.patch(`/admin/qualifiers/${r.id}`, { active: r.disabled });
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const resetPwd = async (r) => {
    const pwd = window.prompt(`Nouveau mot de passe pour ${r.name || r.email} (6 caractères min) :`);
    if (!pwd) return;
    try {
      await api.patch(`/admin/qualifiers/${r.id}`, { password: pwd });
      toast.success("Mot de passe mis à jour.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Supprimer le compte ${r.name || r.email} ?`)) return;
    try {
      await api.delete(`/admin/qualifiers/${r.id}`);
      toast.success("Employé supprimé.");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-500">{"Comptes des employés COP Formation chargés de contacter et d'orienter les prospects."}</p>
        <button onClick={() => setShowForm((v) => !v)} data-testid="crm-add-employee"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-xs font-semibold text-white hover:opacity-90">
          <Plus className="h-4 w-4" /> Nouvel employé
        </button>
      </div>

      {showForm && (
        <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl border border-neutral-200 bg-white p-4 sm:grid-cols-4" data-testid="crm-employee-form">
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nom complet"
            data-testid="employee-name" className="rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
          <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" type="email"
            data-testid="employee-email" className="rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
          <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Mot de passe"
            data-testid="employee-password" className="rounded-md border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand" />
          <button onClick={create} data-testid="employee-submit" className="rounded-md bg-neutral-900 px-3 py-2 text-sm font-semibold text-white hover:bg-neutral-700">Créer le compte</button>
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="px-4 py-3">Employé</th>
              <th className="px-4 py-3 text-center">Traités</th>
              <th className="px-4 py-3 text-center">Orientés</th>
              <th className="px-4 py-3 text-center">Signés</th>
              <th className="px-4 py-3 text-center">Conversion</th>
              <th className="px-4 py-3 text-center">Statut</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-neutral-400">Aucun employé. Créez le premier compte.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`crm-employee-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-neutral-900">{r.name || "—"}</div>
                  <div className="text-xs text-neutral-500">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-center font-semibold">{r.handled}</td>
                <td className="px-4 py-3 text-center">{r.oriented}</td>
                <td className="px-4 py-3 text-center font-semibold text-emerald-700">{r.signed}</td>
                <td className="px-4 py-3 text-center font-bold">{r.conversion}%</td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggle(r)} data-testid={`employee-toggle-${r.id}`}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${r.disabled ? "border-neutral-200 bg-neutral-100 text-neutral-500" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                    {r.disabled ? "Désactivé" : "Actif"}
                  </button>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => resetPwd(r)} className="rounded-md border border-neutral-300 px-2 py-1 text-[11px] font-semibold text-neutral-700 hover:border-neutral-500">Mot de passe</button>
                    <button onClick={() => remove(r)} data-testid={`employee-delete-${r.id}`} className="rounded-md border border-red-200 px-2 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* --------------------------------- Page ---------------------------------- */
export default function AdminCRM() {
  const [tab, setTab] = useState("prospects");
  const [ov, setOv] = useState(null);

  useEffect(() => {
    api.get("/admin/crm/overview").then(({ data }) => setOv(data)).catch(() => {});
  }, [tab]);

  const TABS = [
    ["prospects", "Prospects", KanbanSquare],
    ["schools", "Auto-écoles", Building2],
    ["employees", "Employés", Users],
  ];

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">CRM</h1>
        <p className="mt-2 text-neutral-500">{"Suivi des prospects, évaluation des auto-écoles et des employés."}</p>

        {ov && (
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="crm-kpis">
            <Kpi icon={KanbanSquare} label="Prospects" value={ov.total} />
            <Kpi icon={TrendingUp} label="Conversion" value={`${ov.conversion}%`} accent="text-emerald-700" />
            <Kpi icon={Users} label="Signés" value={ov.signed} accent="text-emerald-700" />
            <Kpi icon={AlertTriangle} label="Relances dues" value={ov.relances_due} accent={ov.relances_due ? "text-amber-700" : "text-neutral-900"} />
            <Kpi icon={Building2} label="Écoles actives" value={ov.active_schools} />
          </div>
        )}

        <div className="mt-6 flex gap-1 border-b border-neutral-200" data-testid="crm-tabs">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`crm-tab-${key}`}
              className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === key ? "border-brand text-brand" : "border-transparent text-neutral-500 hover:text-neutral-800"
              }`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "prospects" && <ProspectsTab />}
          {tab === "schools" && <SchoolsTab />}
          {tab === "employees" && <EmployeesTab />}
        </div>
      </div>
    </Shell>
  );
}
